export const TUTORIAL_DEMO_PATIENT_ID = '00000000-0000-4000-a000-000000000001';
export const TUTORIAL_DEMO_SESSION_ID = '00000000-0000-4000-a000-000000000002';
export const TUTORIAL_DEMO_MEDICAL_RECORD_ID =
	'00000000-0000-4000-a000-000000000003';
export const TUTORIAL_DEMO_MEDICAL_RECORD_SESSION_ID =
	'00000000-0000-4000-a000-000000000004';
export const TUTORIAL_DEMO_ASSESSMENT_ID =
	'00000000-0000-4000-a000-000000000005';
export const TUTORIAL_DEMO_PLAN_ID = '00000000-0000-4000-a000-000000000006';

/** Timestamp fixo para dados da demo — evita recriação a cada render. */
export const TUTORIAL_DEMO_FIXED_NOW = '2026-06-15T12:00:00.000Z';

/** Prefixo padrão para campos pré-preenchidos na demonstração. */
const DEMO_EXAMPLE_TEXT = (body: string) =>
	`[Texto de exemplo — conteúdo fictício, não é um registro real] ${body}`;

/** Nome exibido na videochamada mock (participante remoto). */
export const TUTORIAL_DEMO_MOCK_REMOTE_PARTICIPANT_NAME = 'Paciente teste';

/** Rótulo central no tile do paciente mock na chamada. */
export const TUTORIAL_DEMO_MOCK_REMOTE_PARTICIPANT_TILE_LABEL =
	'PACIENTE (TESTE)';

export const buildTutorialDemonstracaoPatient = (
	professionalId: string,
): Patients.Patient => ({
	id: TUTORIAL_DEMO_PATIENT_ID,
	professional_id: professionalId,
	address_id: null,
	full_name: TUTORIAL_DEMO_MOCK_REMOTE_PARTICIPANT_NAME,
	birth_date: '1998-03-15',
	cpf: '000.000.000-00',
	marital_status: 'SINGLE',
	education_level: 'UNDERGRADUATE',
	phone: '(11) 90000-0000',
	email: 'paciente.demo@exemplo.local',
	guardian_full_name: null,
	emergency_contact: '(11) 90000-0001',
	opened_at: TUTORIAL_DEMO_FIXED_NOW,
	gender: 'PREFER_NOT_TO_SAY',
	rg: null,
	children: [],
	profession: null,
	created_at: TUTORIAL_DEMO_FIXED_NOW,
	updated_at: TUTORIAL_DEMO_FIXED_NOW,
});

export const buildTutorialDemonstracaoMedicalRecord =
	(): MedicalRecords.MedicalRecord => ({
		id: TUTORIAL_DEMO_MEDICAL_RECORD_ID,
		patient_id: TUTORIAL_DEMO_PATIENT_ID,
		status: 'ACTIVE',
		opened_at: TUTORIAL_DEMO_FIXED_NOW,
		started_at: null,
		closed_at: null,
		closure_reason: null,
		closure_summary: null,
		final_referrals: null,
		assessments: [],
		plans: [],
		created_at: TUTORIAL_DEMO_FIXED_NOW,
		updated_at: TUTORIAL_DEMO_FIXED_NOW,
	});

export const buildTutorialDemonstracaoSession = (options: {
	professional: Professionals.Professional;
	patient: Patients.Patient;
}): Sessions.Session => {
	const { professional, patient } = options;
	const selectedDate = TUTORIAL_DEMO_FIXED_NOW;

	return {
		id: TUTORIAL_DEMO_SESSION_ID,
		available_date_id: null,
		service_id: '00000000-0000-4000-a000-000000000010',
		parent_session: null,
		type: 'P2P',
		status: 'STARTED',
		status_description: null,
		payments: [],
		is_online: true,
		display_name: 'Consulta demonstração',
		metadata: null,
		participants: [
			{
				id: '00000000-0000-4000-a000-000000000011',
				session_id: TUTORIAL_DEMO_SESSION_ID,
				entity_type: 'PATIENT',
				patient_id: patient.id,
				user_id: null,
				patient: {
					id: patient.id,
					email: patient.email ?? '',
					full_name: patient.full_name,
					birth_date: patient.birth_date,
					phone: patient.phone,
					cpf: patient.cpf,
					gender: patient.gender,
					profession: patient.profession ?? null,
				},
			},
		],
		scheduled_by: 'PROFESSIONAL',
		selected_date: selectedDate,
		meets_url: null,
		googleCalendarEventId: null,
		scheduling_error: null,
		professional_notes: null,
		professional_private_notes: null,
		user_notes: null,
		user_private_notes: null,
		payment_done_at: null,
		started_at: new Date(selectedDate),
		finished_at: null,
		created_at: new Date(selectedDate),
		updated_at: new Date(selectedDate),
		video_room: null,
		medical_record_session: null,
		professional_id: professional.id,
		professional,
	};
};

export const buildTutorialDemonstracaoMedicalRecordSession =
	(): MedicalRecords.MedicalRecordSession => ({
		id: TUTORIAL_DEMO_MEDICAL_RECORD_SESSION_ID,
		medical_record_id: TUTORIAL_DEMO_MEDICAL_RECORD_ID,
		session_id: TUTORIAL_DEMO_SESSION_ID,
		session_number: 1,
		attendance: 'PRESENT',
		observed_mood: DEMO_EXAMPLE_TEXT(
			'Humor observado na demonstração: colaborativo e receptivo ao diálogo.',
		),
		observed_mood_tag: null,
		secondary_complaints: DEMO_EXAMPLE_TEXT(
			'Queixas secundárias fictícias apenas para ilustrar como a IA preenche este campo.',
		),
		secondary_complaints_tag: null,
		content_and_techniques: DEMO_EXAMPLE_TEXT(
			'Conteúdo trabalhado e técnicas utilizadas — texto de exemplo para a demonstração.',
		),
		content_and_techniques_tag: null,
		evolution_notes: DEMO_EXAMPLE_TEXT(
			'Evolução e observações fictícias mostrando o formato do pré-preenchimento.',
		),
		evolution_notes_tag: null,
		risk_indicator: 'NONE',
		risk_notes: null,
		risk_notes_tag: null,
		raw_notes: DEMO_EXAMPLE_TEXT(
			'Anotações livres de exemplo. Nenhuma informação aqui corresponde a um atendimento real.',
		),
		raw_notes_tag: null,
		summary:
			'Resumo da sessão gerado pela IA na demonstração. Este parágrafo é fictício e mostra como o resultado do pós-processamento aparece no prontuário.',

		summary_tag: null,
		short_summary: DEMO_EXAMPLE_TEXT('Resumo curto de exemplo.'),
		short_summary_tag: null,
		pos_process_status: 'COMPLETED',
		created_at: TUTORIAL_DEMO_FIXED_NOW,
		updated_at: TUTORIAL_DEMO_FIXED_NOW,
		tasks: [],
	});

export const buildTutorialDemonstracaoMedicalRecordWithAiContent =
	(): MedicalRecords.MedicalRecord => ({
		...buildTutorialDemonstracaoMedicalRecord(),
		started_at: TUTORIAL_DEMO_FIXED_NOW,
		assessments: [
			{
				id: TUTORIAL_DEMO_ASSESSMENT_ID,
				medical_record_id: TUTORIAL_DEMO_MEDICAL_RECORD_ID,
				assessed_at: TUTORIAL_DEMO_FIXED_NOW,
				assessment_number: 1,
				instruments_used: 'Entrevista clínica (texto de exemplo)',
				clinical_impression: DEMO_EXAMPLE_TEXT(
					'Impressão clínica fictícia para demonstrar o pré-preenchimento do histórico.',
				),
				icd10_primary: 'F41.1',
				icd10_secondary: null,
				differential_diagnosis: DEMO_EXAMPLE_TEXT(
					'Diagnósticos diferenciais de exemplo — não refletem um caso real.',
				),
				bai_score: 18,
				bai_level: 'MILD',
				bdi_score: 12,
				bdi_level: 'MINIMAL',
				deleted_at: null,
				created_at: TUTORIAL_DEMO_FIXED_NOW,
				updated_at: TUTORIAL_DEMO_FIXED_NOW,
			},
		],
		plans: [
			{
				id: TUTORIAL_DEMO_PLAN_ID,
				medical_record_id: TUTORIAL_DEMO_MEDICAL_RECORD_ID,
				plan_date: TUTORIAL_DEMO_FIXED_NOW,
				version: 1,
				active: true,
				theoretical_approach: 'Abordagem de exemplo (demonstração)',
				frequency: 'Semanal (exemplo)',
				session_duration_min: 50,
				format: 'Online (exemplo)',
				estimated_duration:
					'Duração estimada de exemplo — conteúdo fictício para demonstração.',
				objectives: DEMO_EXAMPLE_TEXT(
					'Objetivos terapêuticos fictícios ilustrando como a IA preenche o plano.',
				),
				planned_techniques: DEMO_EXAMPLE_TEXT(
					'Técnicas planejadas de exemplo. Não representam um plano terapêutico real.',
				),
				referrals: null,
				created_at: TUTORIAL_DEMO_FIXED_NOW,
				updated_at: TUTORIAL_DEMO_FIXED_NOW,
			},
		],
	});

export const buildTutorialDemonstracaoEndedSession = (options: {
	professional: Professionals.Professional;
	patient: Patients.Patient;
}): Sessions.Session => {
	const base = buildTutorialDemonstracaoSession(options);
	const recordSession = buildTutorialDemonstracaoMedicalRecordSession();

	return {
		...base,
		status: 'FINISHED',
		finished_at: new Date(TUTORIAL_DEMO_FIXED_NOW),
		medical_record_session: {
			id: recordSession.id,
			session_number: recordSession.session_number,
			summary: recordSession.summary,
			short_summary: recordSession.short_summary,
		},
	};
};
